/**
 * **文档库在宿主里的落点**：当前是哪一份、什么时候存、导入撞车了怎么办。
 *
 * 纯逻辑（记录 / UUID / 落盘 / 增删改查）全在 `graph/documentLibrary.ts`，
 * 这里只做"把它接到画布上"这件事。
 *
 * ## 三件事
 *
 * ```
 *   ① 当前文档   谁在编辑（id + 名字），名字同时喂给属性面板 / 菜单
 *   ② 自动保存   图一变就（防抖）写回库里那一条 —— 用户不必记得按保存
 *   ③ 导入       文件带 UUID 撞上库里已有的 → 交给宿主弹窗问（覆盖 / 另存 / 取消）
 * ```
 *
 * ## ⚠️ 只有"有身份的画布"才自动保存
 *
 * 刚打开编辑器时画布是空的、**不属于任何文档**（`currentId === null`）——
 * 那种状态下不自动建文档：随手拖两个节点就在库里留下一份垃圾，
 * 比"没保存"更烦人。用户点「新建文档」或者「保存到文档库」之后
 * 这份画布才有身份，之后才自动保存。
 *
 * ## ⚠️ "图变了"用**数组引用**判定，不做深比较
 *
 * 拖动期间位置是**就地写**的（见 `graph/documentSync.ts`），深比较会
 * 每帧遍历整张图；而本仓库有一条硬规矩：每次文档变更**必须换数组引用**
 * （不换的话 `nodes.value = …` 对 Vue 是空操作，多窗口同步就断了）。
 * 所以监听引用就够了 —— 又准又便宜。
 */
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { DOCUMENT_VERSION } from '@/graph/serialization'
import type { TemplateFile } from '@/graph/templateFile'
import type { PackageExample } from '@/node/package'
import { parseTemplateFile } from '@/graph/templateFile'
import {
  createBestStore,
  createMemoryStore,
  type DocumentStore,
  type StoreKind,
} from '@/graph/documentStore'
import {
  UNNAMED_DOCUMENT,
  documentSize,
  duplicateDocument,
  emptyLibrary,
  fileToRecord,
  findDocument,
  importConflictOf,
  newDocumentId,
  recordToFile,
  removeDocument,
  renameDocument,
  safeDocumentName,
  sortedDocuments,
  upsertDocument,
  type DocumentRecord,
  type LibraryState,
  type WriteResult,
} from '@/graph/documentLibrary'

export interface UseDocumentsCtx {
  /** 宿主 props 给的初始身份（集成方指定的文档）—— 没有就是"还不属于任何文档" */
  initial?: { id?: string; name?: string }
  /** 图的引用（只用来判"变没变"，见文件头那条） */
  nodes: { value: unknown }
  edges: { value: unknown }
  /** 当前图的模板文件（`useTemplateFile.currentFile`） */
  currentFile: (meta?: { id?: string; name?: string }) => TemplateFile
  /** 把一份文件装进画布（`useTemplateFile.applyFile`） */
  applyFile: (file: TemplateFile) => boolean
  /** 清空画布（新建文档时用；走宿主那条已有实现，手势都收拾干净） */
  clearGraph: () => void
  /**
   * **清空撤销历史**（换文档时必须调）。
   *
   * ⚠️ 和 `clearGraph` 分开：清空画布是**可以撤销**的（菜单里明说了），
   * 而"换了一份文档"不是 —— 历史里那份快照属于**另一份文档**，
   * 留着它按一次 Ctrl+Z 就把旧文档的图装进新文档里。
   */
  resetHistory: () => void
  /*
   * ⚠️ 这里**没有** `confirm` 了（2027 删）：确认对话框全在宿主那边
   * （`askConfirm()` + `NpInfoDialog`），而这个 ctx 里的那份**从来没被用过** ——
   * 留着它就是给下一个人一个"看起来该在这里问一句"的错觉，
   * 而真正的规矩是"问不问是宿主的事"（组件与这个 composable 都不弹系统框）。
   */
  /**
   * **一份已经存在的文档被装进画布之后**（打开 / 恢复上次那份 / 导入）。
   *
   * ⚠️ `reason` 是给宿主分场合的，两种情况**必须**区别对待：
   *   · `'open'`    —— 用户刚点了"打开/导入"，他要看的就是这份文档
   *                    （没有画布窗口时，宿主会把当前窗口换成节点图）；
   *   · `'restore'` —— 刷新时自动打开上次那份，**用户还没表态**：
   *                    这时把首页（文档窗口）换成画布，等于把"默认窗口"
   *                    这件事取消了。
   *
   * 新建空文档**不**走这条：那一步用户还在列表里挑挑拣拣。
   */
  onOpened?: (reason: 'open' | 'restore') => void
  showNotice: (message: string) => void
  /**
   * 落盘目标。不给就自己挑一个（**IndexedDB 优先，localStorage 兜底**，
   * 见 `graph/documentStore.ts`）；测试喂一个内存 store。
   */
  store?: DocumentStore
  /** 自动保存的防抖（毫秒）。测试里调小到 0 就不用等 */
  autosaveMs?: number
  /**
   * **要不要在启动时把上次那份文档打开**（默认要）。
   *
   * 用户明确要的那条："刷新之后自动打开上次那份"。测试里关掉，
   * 免得每个用例都被上一次的库影响。
   */
  restoreLast?: boolean
}

/** 导入一份文件之后发生了什么 —— 冲突时**先不落地**，等用户选 */
export interface ImportOutcome {
  ok: boolean
  /** 失败原因（不是我们的文件 / JSON 坏了） */
  error?: string
  /** 撞车了：库里已经有一份同 UUID 的，等用户裁决 */
  conflict?: { existing: DocumentRecord; incoming: DocumentRecord }
}

export interface UseDocuments {
  /**
   * **库读完了吗**。
   *
   * ⚠️ IndexedDB 是异步的，所以"库里有哪些文档"这件事在启动的头几毫秒
   * 是没有答案的 —— 界面（文档窗口）要据此显示"正在读…"，
   * 而不是显示一个骗人的"库里还没有文档"。
   */
  ready: Ref<boolean>
  /** 文档存在哪儿（界面上要写清 → 用户才知道刷新之后还在不在） */
  storeKind: Ref<StoreKind>
  /** 库里的文档（最近改过的在前） */
  documents: ComputedRef<DocumentRecord[]>
  /** 当前文档 id（null = 这份画布还不属于任何文档） */
  currentId: Ref<string | null>
  /** 当前文档名（没有身份时用宿主 props 的名字，再没有就是"未命名文档"） */
  currentName: ComputedRef<string>
  /** 图改过、还没存进去 */
  dirty: Ref<boolean>
  /** 库里有多少份 / 合计多少节点（界面上那句"多大"） */
  summary: ComputedRef<{ count: number; nodes: number; edges: number }>
  /** 正在等用户裁决的那次导入（宿主据此弹窗） */
  pendingImport: Ref<{ file: TemplateFile; existing: DocumentRecord } | null>
  /** 新建一份空文档，并把它变成当前文档 */
  create: (name?: string) => DocumentRecord
  /** 打开库里的一份（会先问"替换当前画布吗"） */
  open: (id: string) => boolean
  /** 把当前画布存进库里（没有身份就先建一份） */
  save: () => WriteResult
  rename: (id: string, name: string) => void
  remove: (id: string) => void
  duplicate: (id: string) => DocumentRecord | undefined
  /** 导出成文件（下载） */
  exportFile: (id: string) => TemplateFile | undefined
  /** 导入一段文件内容（不弹窗；冲突信息回给宿主） */
  importText: (text: string) => ImportOutcome
  /**
   * 导入一份**已经在内存里的**文件（示例文档走这条：它由包的代码生成，
   * 不必先序列化成字符串再解析一遍）。撞车判定与 `importText` 完全同源。
   */
  importFile: (file: TemplateFile) => ImportOutcome
  /** 打开一份**示例文档**（包带来的；走的就是上面那条导入路） */
  openExample: (example: PackageExample, make: () => TemplateFile) => ImportOutcome
  /** 用户对冲突的裁决 */
  resolveImport: (choice: 'overwrite' | 'keep') => boolean
}

export function useDocuments(ctx: UseDocumentsCtx): UseDocuments {
  /*
   * ⚠️ store 是**异步**备好的（IndexedDB 要先开库）：测试给内存 store 时
   * 那一拍几乎立刻就到，浏览器里也就几毫秒 —— 但它确实是异步的，
   * 所以下面有个 `ready`，界面不能把"还没读完"当成"库里是空的"。
   */
  const storeRef = ref<DocumentStore>(ctx.store ?? createMemoryStore())
  const ready = ref(false)
  const storeKind = ref<StoreKind>(ctx.store?.kind ?? 'memory')

  const state = ref<LibraryState>(emptyLibrary())
  const currentId = ref<string | null>(ctx.initial?.id ?? null)
  const dirty = ref(false)
  const pendingImport = ref<{ file: TemplateFile; existing: DocumentRecord } | null>(null)
  /** 我们自己刚装过图 —— 那一拍不该被当成"用户改了图" */
  let applying = false
  /**
   * 上一次打开的是哪一份（**"刷新之后自动打开上次那份"**的落点）。
   *
   * ⚠️ 它存在**库那条记录里**（`LibraryState.lastId`），不是另开一个键：
   * 文档被删掉时它自然跟着失效（`findDocument` 查不到就当没有）。
   */
  let restoreId: string | null = null

  async function boot(): Promise<void> {
    if (!ctx.store) {
      const store = await createBestStore()
      storeRef.value = store
      storeKind.value = store.kind
    }
    const loaded = await storeRef.value.load()
    state.value = loaded
    restoreId = loaded.lastId ?? null
    ready.value = true

    /* 宿主指定的 id 如果库里真有，名字以库里为准（本地改过名） */
    const declared = findDocument(state.value, currentId.value)
    if (declared) currentId.value = declared.id

    /*
     * ★ **刷新之后自动打开上次那份**（用户要的那条）。
     * 三种情况都不动：
     *   · 宿主明确指定了文档（集成方的意图优先）；
     *   · 上次那份已经被删了（`findDocument` 查不到）；
     *   · 关掉了 `restoreLast`（测试用）。
     */
    if (ctx.restoreLast === false || ctx.initial?.id) return
    const last = findDocument(state.value, restoreId)
    if (!last) return
    applying = true
    if (ctx.applyFile(recordToFile(last))) {
      currentId.value = last.id
      ctx.resetHistory()
      ctx.onOpened?.('restore')
    }
    dirty.value = false
    applying = false
  }

  /** 落盘 + 报错（存不下要**说出来**，静默丢掉一份文档是最坏的结果） */
  function persist(): WriteResult {
    const snapshot = state.value
    void storeRef.value.save(snapshot).then((result) => {
      if (!result.ok) ctx.showNotice(result.message)
    })
    /* 乐观返回：内存里的库已经更新了；失败会由上面那条提示补上 */
    return { ok: true }
  }

  const documents = computed(() => sortedDocuments(state.value))
  const currentRecord = computed(() => findDocument(state.value, currentId.value))
  const currentName = computed(
    () => currentRecord.value?.name ?? safeDocumentName(ctx.initial?.name),
  )
  const summary = computed(() => {
    let nodes = 0
    let edges = 0
    for (const record of state.value.documents) {
      const size = documentSize(record.document)
      nodes += size.nodes
      edges += size.edges
    }
    return { count: state.value.documents.length, nodes, edges }
  })

  function setState(next: LibraryState): WriteResult {
    state.value = next
    return persist()
  }

  /**
   * 当前文档变了 → 记进库里（"刷新之后自动打开上次那份"靠它）。
   *
   * ⚠️ 用一个**独立的写**，而不是塞进每条记录里：`lastId` 是"这次会话"
   * 的状态，和某一份文档的内容无关（改一份文档不该顺手改它的 `updatedAt`）。
   */
  function rememberCurrent(): void {
    if (state.value.lastId === currentId.value) return
    setState({ ...state.value, lastId: currentId.value })
  }

  /**
   * 把当前画布写进某一份记录（**不落盘**，调用方决定什么时候落）。
   *
   * 用在"导出当前这一份"上：画布上的内容可能比库里那份新
   * （自动保存有 700ms 防抖），导出要的是**眼前这一份**。
   */
  function recordOfCurrent(record: DocumentRecord): DocumentRecord {
    const file = ctx.currentFile()
    return {
      ...record,
      document: file.document,
      ...(file.tool ? { tool: file.tool } : {}),
      updatedAt: Date.now(),
    }
  }

  function save(): WriteResult {
    const existing = currentRecord.value
    /* ⚠️ 只构造一次：`currentFile()` 要把整张图序列化一遍，调两次是白花钱 */
    const file = ctx.currentFile()
    if (!existing) {
      /* 还没有身份：这一下就给它一个（"保存到文档库"就是这个意思） */
      const id = currentId.value ?? newDocumentId()
      const record: DocumentRecord = {
        id,
        name: safeDocumentName(currentName.value),
        document: file.document,
        ...(file.tool ? { tool: file.tool } : {}),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      const result = setState(upsertDocument(state.value, record))
      if (result.ok) {
        currentId.value = id
        dirty.value = false
      }
      return result
    }
    const result = setState(
      upsertDocument(state.value, {
        ...existing,
        document: file.document,
        ...(file.tool ? { tool: file.tool } : {}),
        updatedAt: Date.now(),
      }),
    )
    if (result.ok) dirty.value = false
    return result
  }

  function create(name?: string): DocumentRecord {
    const now = Date.now()
    const record: DocumentRecord = {
      id: newDocumentId(),
      name: safeDocumentName(name ?? '新建文档'),
      /* 空文档：版本号必须跟上 —— 少了它 `fromDocument` 那一路读不下去 */
      document: { version: DOCUMENT_VERSION, nodes: [], edges: [] },

      createdAt: now,
      updatedAt: now,
    }
    applying = true
    ctx.clearGraph()
    ctx.resetHistory()
    setState(upsertDocument(state.value, record))
    currentId.value = record.id
    dirty.value = false
    /*
     * ⚠️ **同步**放闸 —— 和自动保存那个 watcher 的 `flush: 'sync'` 配成一对：
     * 闸门圈定的就是"这几行同步代码"，microtask 那种写法会让闸门在
     * 下一次 flush 之后才打开（而那时用户的改动已经被当成"装图"跳过了）。
     */
    applying = false
    return record
  }

  function open(id: string): boolean {
    const record = findDocument(state.value, id)
    if (!record) {
      ctx.showNotice('这份文档不在库里了（可能被删掉了）')
      return false
    }
    applying = true
    const ok = ctx.applyFile(recordToFile(record))
    /*
     * ⚠️ 清历史**由这里负责**（不指望宿主在 `applyFile` 里做）：
     * "当前在编哪一份"是这个 composable 的状态，那"历史属于上一份文档、
     * 换文档就得清"也该由它保证。宿主那边顺手清一次也无妨（幂等）。
     */
    ctx.resetHistory()
    currentId.value = record.id
    dirty.value = false
    /* ⚠️ **同步**放闸（不是 microtask）：见自动保存那个 watcher 的注释 */
    applying = false
    if (ok) ctx.onOpened?.('open')
    return ok
  }

  function rename(id: string, name: string): void {
    setState(renameDocument(state.value, id, name))
  }

  function remove(id: string): void {
    setState(removeDocument(state.value, id))
    if (currentId.value === id) {
      /*
       * ⚠️ 删掉的正好是当前这一份：**画布不动**（用户的图不能被一次删除抹掉），
       * 但身份没了 —— 它回到"还不属于任何文档"，再保存会建一份新的。
       */
      currentId.value = null
      dirty.value = true
      ctx.showNotice('已从库里删掉；画布还留着，按「保存到文档库」可以再存一份')
      return
    }
    ctx.showNotice('已从库里删掉')
  }

  function duplicate(id: string): DocumentRecord | undefined {
    const result = duplicateDocument(state.value, id)
    if (!result.record) return undefined
    setState(result.state)
    ctx.showNotice(`已复制：${result.record.name}`)
    return result.record
  }

  function exportFile(id: string): TemplateFile | undefined {
    const record = findDocument(state.value, id)
    if (!record) return undefined
    /* 导出的就是当前这一份的话，用**画布上最新的**（可能还没自动保存） */
    const source = id === currentId.value ? recordOfCurrent(record) : record
    return recordToFile(source)
  }

  /**
   * 把一份文件收进库里（并变成当前文档）。
   *
   * `id` 用**文件里那个**（用户要的 UUID 语义：同一份文档在哪都是同一个 id）。
   */
  function adopt(file: TemplateFile): boolean {
    const record = fileToRecord(file, UNNAMED_DOCUMENT)
    applying = true
    setState(upsertDocument(state.value, record))
    ctx.applyFile(file)
    ctx.resetHistory()
    currentId.value = record.id
    dirty.value = false
    applying = false
    ctx.onOpened?.('open')
    return true
  }

  /** 一段文本 → 文件（解析失败就是"不是我们的文件"） */
  function importText(text: string): ImportOutcome {
    const result = parseTemplateFile(text)
    if (!result.ok || !result.file) return { ok: false, error: result.error ?? '未知原因' }
    return importFile(result.file)
  }

  /**
   * ★★ **一份文件进库**（所有"打开"的路最后都汇到这儿）。
   *
   * 判据是**文件里带的 UUID**：库里已经有同一个 → **先不落地**，
   * 把冲突交给宿主弹窗（覆盖 / 另存为 / 取消）。
   * 为什么老文件（没有 UUID）不算撞车，见 `importConflictOf`。
   */
  function importFile(file: TemplateFile): ImportOutcome {
    const existing = importConflictOf(state.value, file)
    if (existing) {
      const incoming = fileToRecord(file, existing.name)
      pendingImport.value = { file, existing }
      return { ok: true, conflict: { existing, incoming } }
    }
    adopt(file)
    ctx.showNotice(`已收进文档库：${safeDocumentName(file.name)}`)
    return { ok: true }
  }

  /**
   * ★ **打开一份示例文档**（「文件 → 示例文件」走这条）。
   *
   * ⚠️ `make` 是**延迟构造**的：示例的图要在"确实要用它"的时候才造
   * （撞车时用户可能选取消 —— 那就白造一份图了），而且每次都要是新的一份
   * （见 `PackageExample.graph` 的说明）。
   *
   * 走的完全就是 `importFile` 那条路：**示例文档也是文档** ——
   * 它有固定 UUID，所以第二次打开同一个示例时会问"要不要覆盖"
   * （示例更新了 → 覆盖；自己改过 → 另存为）。这条规则在这里第一次
   * 有了真实用途，不是特意为示例加的。
   */
  function openExample(example: PackageExample, make: () => TemplateFile): ImportOutcome {
    const file = make()
    return importFile({ ...file, id: example.uuid, name: example.name })
  }

  function resolveImport(choice: 'overwrite' | 'keep'): boolean {
    const pending = pendingImport.value
    if (!pending) return false
    pendingImport.value = null

    if (choice === 'overwrite') {
      /*
       * ⚠️ **只覆盖内容与名字，`createdAt` 保留**：那份文档是什么时候有的
       * 是事实，不会因为一次覆盖而改变（同 `upsertDocument` 的规矩）。
       */
      const next: DocumentRecord = {
        ...pending.existing,
        name: safeDocumentName(pending.file.name ?? pending.existing.name),
        document: pending.file.document,
        ...(pending.file.tool ? { tool: pending.file.tool } : {}),
        updatedAt: Date.now(),
      }
      applying = true
      setState(upsertDocument(state.value, next))
      ctx.applyFile(pending.file)
      ctx.resetHistory()
      currentId.value = next.id
      dirty.value = false
      applying = false
      ctx.onOpened?.('open')
      ctx.showNotice(`已覆盖：${next.name}`)
      return true
    }

    /*
     * ★★★ 「另存为新文档」= **换一个新 UUID**。
     *
     * ⚠️ 直接 `adopt(pending.file)` 是个真 bug：文件里带的还是**原来那个
     * UUID**，而 `adopt` 是按 id upsert 的 —— 于是"另存为"会**覆盖掉
     * 刚才那份**，两份只留一份，和弹窗上写的"两份都留着"正好相反。
     * （`verify:documents` 里那条断言当场抓到了。）
     */
    adopt({ ...pending.file, id: newDocumentId() })
    ctx.showNotice(`已另存为新文档：${safeDocumentName(pending.file.name)}`)
    return true
  }

  /*
   * ── 自动保存 ────────────────────────────────────────────────
   *
   * 只监听**引用**（见文件头）：`nodes.value = [...]` 这条规矩让
   * "图变没变"变得又准又便宜。
   */
  let timer: ReturnType<typeof setTimeout> | null = null
  watch(
    [() => ctx.nodes.value, () => ctx.edges.value],
    () => {
      /*
       * ⚠️ `ready` 那道闸必须有：库还没读完时 `currentId` 可能是宿主给的
       * 初始值，而这时候"图变了"只是**装配过程本身**（第一次渲染）——
       * 不管它就会在建好之前先写一份空文档进库。
       */
      if (!ready.value || applying || !currentId.value) return
      dirty.value = true
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        save()
      }, ctx.autosaveMs ?? 700)
    },
    /*
     * ⚠️⚠️ **必须 `flush: 'sync'`**，这一条是实测踩出来的（不是保守写法）：
     *
     * `applying` 那道闸靠"装图的这一段同步代码"圈定范围，而默认的
     * `flush: 'pre'` 是**微任务**里跑的 —— 只要这段代码里还有**别的**
     * watcher 被触发（`create()` 里改 `currentId` 就会），Vue 会先排一次
     * flush；而"清 `applying`"那个 Promise 回调排在它**后面**。
     * 结果：图变化那一拍看到 `applying === true`，被当成"装图"跳过 ——
     * **画布改了却永远不标脏、也永远不自动保存**（静默的那种坏法）。
     *
     * `sync` 之下，回调就在赋值那一刻跑，"装图那段同步代码"这个范围
     * 才真的成立。
     */
    { flush: 'sync' },
  )

  /*
   * ── 「刷新之后打开上次那份」要记的那个 id ──────────────────
   *
   * 当前文档一变就写进库（`lastId`）。放在 watcher 里而不是每条路径上
   * 手动调：新建 / 打开 / 导入 / 删除 / 覆盖 —— 五条路都要记，
   * 漏一条的表现是"刷新之后回到了另一份文档"，很难查。
   */
  watch(
    currentId,
    () => {
      if (ready.value) rememberCurrent()
    },
    { flush: 'sync' },
  )

  /* 启动（异步：IndexedDB 要先开库） */
  void boot()

  return {
    ready,
    storeKind,
    documents,
    currentId,
    currentName,
    dirty,
    summary,
    pendingImport,
    create,
    open,
    save,
    rename,
    remove,
    duplicate,
    exportFile,
    importText,
    importFile,
    openExample,
    resolveImport,
  }
}
